import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  MagnifyingGlass,
  Receipt,
  CurrencyInr,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReceiptViewDialog from "@/components/ReceiptViewDialog";
import { useAuth } from "@/contexts/AuthContext";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function monthLabel(m) {
  if (!m || m.length < 7) return null;
  const y = parseInt(m.slice(0, 4), 10);
  const mm = parseInt(m.slice(5, 7), 10);
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!y || mm < 1 || mm > 12) return m;
  return `${names[mm - 1]} ${y}`;
}

// Static fallback list of academic-year months (Apr → Mar). The dropdown
// uses every month seen across the loaded payments PLUS this fallback so
// teachers can filter to upcoming months too.
function buildMonthOptions(items) {
  const seen = new Set();
  items.forEach((p) => {
    if (p.period_month) seen.add(p.period_month);
  });
  return Array.from(seen).sort();
}

export default function Payments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sumAmount, setSumAmount] = useState(0);
  // Phase 25C — backend strips sum_amount for cashier role; track that so
  // we hide the "Filtered total" KPI card entirely (don't show ₹0 — that
  // would mislead the counter staff).
  const [moneyTotalsHidden, setMoneyTotalsHidden] = useState(false);
  // Teacher privacy — cumulative receipt count/₹ hidden; show self-scoped
  // "today's receipts" from /operations/me/today instead.
  const [todayMine, setTodayMine] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [filterSession, setFilterSession] = useState("__all__");
  const [filterClass, setFilterClass] = useState("__all__");
  const [filterMode, setFilterMode] = useState("__all__");
  const [filterMonth, setFilterMonth] = useState("__all__");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";

  // Receipt viewer state — clicking row opens read-only receipt dialog
  const [viewingPayment, setViewingPayment] = useState(null);

  const filteredClasses = useMemo(() => {
    if (filterSession === "__all__") return classes;
    return classes.filter((c) => c.session_id === filterSession);
  }, [classes, filterSession]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSession !== "__all__") params.set("session_id", filterSession);
      if (filterClass !== "__all__") params.set("class_id", filterClass);
      if (filterMode !== "__all__") params.set("payment_mode", filterMode);
      if (filterMonth !== "__all__") params.set("period_month", filterMonth);
      if (search) params.set("search", search);
      const url = `/school/fee-payments${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } = await api.get(url);
      setItems(data.items);
      setTotal(data.total);
      setSumAmount(data.sum_amount ?? 0);
      setMoneyTotalsHidden(data.sum_amount === null || data.sum_amount === undefined);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filterSession, filterClass, filterMode, filterMonth, search]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get(isTeacher ? "/school/teacher/me/classes" : "/school/classes"),
          api.get("/school/sessions"),
        ]);
        setClasses(c.data);
        setSessions(s.data);
        if (isTeacher) {
          const t = await api.get("/school/operations/me/today");
          setTodayMine(t.data);
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [isTeacher]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const monthOptions = useMemo(() => buildMonthOptions(items), [items]);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
          {isTeacher ? "My collections" : "Finance"}
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
          {isTeacher ? "Receipt history" : "Payments"}
          {!isTeacher && <span className="text-slate-400 font-normal"> ({total})</span>}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {isTeacher
            ? "All receipts you and other staff have collected for students in your assigned classes. Click any row to re-view, download, or share the receipt."
            : "All fee receipts for your school. Filter by class, session, payment mode or month. Click any row to view the receipt."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {isTeacher ? (
          <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="payments-today-mine">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Today's receipts (mine)</div>
            <div className="text-3xl font-bold text-slate-900 font-heading mt-1 font-mono">
              {todayMine?.today?.receipts ?? 0}
            </div>
            <div className="text-xs text-slate-500 mt-1">collected by you today</div>
          </div>
        ) : !moneyTotalsHidden ? (
          <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="payments-filtered-total">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1">
              <span>Filtered total (raw ₹)</span>
              <span
                title="RAW Σ of all visible receipts (non-voided). Matches the Reports page 'Receipts received (RAW)' figure for the same filters. For 'maps to liability' totals see the Reports page 'Collected (in range)' KPI."
                className="cursor-help text-slate-400 normal-case tracking-normal"
              >
                ⓘ
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900 font-heading mt-1 font-mono">
              {formatINR(sumAmount)}
            </div>
            <div className="text-xs text-slate-500 mt-1">{total} receipt{total === 1 ? "" : "s"}</div>
          </div>
        ) : (
          <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="payments-receipt-count">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Receipts</div>
            <div className="text-3xl font-bold text-slate-900 font-heading mt-1 font-mono">{total}</div>
            <div className="text-xs text-slate-500 mt-1">matching filters</div>
          </div>
        )}
        <div className="p-5 border border-slate-200 bg-white rounded-lg sm:col-span-2 flex items-center justify-center text-sm text-slate-500">
          <Receipt size={28} className="text-slate-300 mr-2" />
          {isTeacher
            ? "Open any receipt to download a fresh PDF or re-share on WhatsApp."
            : "Click any receipt to download PDF or share on WhatsApp."}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search receipt no, head, txn ref…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="payments-search-input"
            />
          </div>
          <Select value={filterSession} onValueChange={(v) => { setFilterSession(v); setFilterClass("__all__"); }}>
            <SelectTrigger className="w-40 h-9" data-testid="payments-filter-session"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sessions</SelectItem>
              {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-44 h-9" data-testid="payments-filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMode} onValueChange={setFilterMode}>
            <SelectTrigger className="w-36 h-9" data-testid="payments-filter-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All modes</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-40 h-9" data-testid="payments-filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All months</SelectItem>
              {monthOptions.map((m) => (
                <SelectItem key={m} value={m} data-testid={`payments-filter-month-${m}`}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Receipt</th>
                <th className="text-left font-semibold px-4 py-3">Student</th>
                <th className="text-left font-semibold px-4 py-3">Head</th>
                <th className="text-left font-semibold px-4 py-3">Month</th>
                <th className="text-right font-semibold px-4 py-3">Amount</th>
                <th className="text-left font-semibold px-4 py-3">Mode</th>
                <th className="text-left font-semibold px-4 py-3">Collected by</th>
                <th className="text-right font-semibold px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-12 py-12 text-center" data-testid="payments-empty">
                    <CurrencyInr size={32} className="mx-auto text-slate-300 mb-2" />
                    <div className="text-sm text-slate-500">No payments yet for this filter.</div>
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setViewingPayment(p)}
                    data-testid={`payment-row-${p.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-900 font-semibold">
                      {p.receipt_no}
                      {p.is_voided && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9px] font-bold uppercase" data-testid={`payment-voided-${p.id}`}>
                          Voided
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{p.student_name}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {p.student_admission_no} · {p.class_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.head_snapshot}</td>
                    <td className="px-4 py-3">
                      {p.period_month ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-blue-100 text-blue-800" data-testid={`payment-month-${p.id}`}>
                          {monthLabel(p.period_month)}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">
                      {formatINR(p.amount_paid)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-100 text-slate-700 uppercase">
                        {p.payment_mode}
                      </span>
                      {p.txn_ref && <div className="text-[10px] text-slate-500 font-mono mt-0.5">{p.txn_ref}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{p.collected_by_name}</td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {new Date(p.created_at).toLocaleDateString()}
                      <div className="text-[10px]">{new Date(p.created_at).toLocaleTimeString()}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receipt viewer — read-only re-open of historic receipt with print/PDF/WhatsApp */}
      {viewingPayment && (
        <ReceiptViewDialog
          open={!!viewingPayment}
          onOpenChange={(v) => { if (!v) setViewingPayment(null); }}
          paymentId={viewingPayment.id}
          onChanged={load}
        />
      )}
    </div>
  );
}
